// frontend/src/lib/api/providerService.ts
// CLEANED VERSION - Provider operations only, services moved to providerServicesService.ts

import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface Provider {
  provider_id: number;
  user_id: number;
  business_name: string;
  business_type: string;
  description?: string;
  phone: string;
  email: string;
  website?: string;
  address?: string;
  city: string;
  state: string;
  zip_code?: string;
  latitude?: number;
  longitude?: number;
  is_verified: boolean;
  is_active: boolean;
  verification_status: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  total_services: number;
  average_rating: number;
  total_bookings: number;
  years_experience: number;
  rating: number;
  total_reviews: number | string;
}

export interface ProviderProfile {
  provider_id: number;
  user_id: number;
  business_name: string;
  description: string;
  years_experience: number;
  business_type: string;
  business_license?: string;
  insurance_details?: string;
  website?: string;
  business_phone: string;
  business_email: string;
  about_me: string;
  specializations: string[];
  certifications: string[];
  profile_image?: string;
  provider_slug_url?: string;
  is_active: boolean;
  is_verified: boolean;
  verification_status: 'pending' | 'verified' | 'rejected';
  rating: number;
  total_reviews: number;
  services_count: number;
  areas_count: number;
  created_at: string;
  updated_at: string;
}

export interface BusinessProfile {
  business_name: string;
  description: string;
  years_experience: number;
  business_type: 'individual' | 'company' | 'partnership' | 'small_business' | 'llc' | 'corporation';
  business_license?: string;
  insurance_details?: string;
  website?: string;
  business_phone: string;
  business_email: string;
  about_me: string;
  specializations: string;  // Changed from string[] to string for form handling
  certifications: string;   // Changed from string[] to string for form handling
  profile_image?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  provider_slug_url?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  preferred_language?: string;
}

export interface ProviderFilters {
  page?: number;
  limit?: number;
  search?: string;
  city?: string;
  verified?: string;
  status?: string;
  rating?: string;
}

export interface ProviderFormData {
  business_name: string;
  business_type: string;
  business_email: string;
  contact_person: string;
  contact_phone: string;
  contact_email: string;
  description?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  years_experience?: number;
}

export interface ProviderStats {
  total_providers: number;
  verified_providers: number;
  active_providers: number;
  pending_verification: number;
  avg_rating: number;
  total_services: number;
}

export interface ServiceArea {
  area_id: number;
  provider_id: number;
  pincode: string;
  area_name: string;
  city: string;
  state: string;
  is_active: boolean;
  created_at: string;
}

export interface ProviderServiceOffering {
  service_id: number;
  service_name: string;
  description?: string;
  price_range?: string;
  estimated_duration?: string;
  is_active: boolean;
  created_at: string;
}

export interface BookingRequest {
  booking_id: number;
  customer_name: string;
  service_name: string;
  requested_date: string;
  status: string;
  total_amount?: number;
  created_at: string;
}

export interface WorkingHours {
  day: string;
  is_available: boolean;
  start_time?: string;
  end_time?: string;
}

export interface ProviderReview {
  review_id: number;
  customer_name: string;
  rating: number;
  comment?: string;
  service_name: string;
  created_at: string;
  response?: string;
  response_date?: string;
}

export interface ProviderEarnings {
  total_earnings: number;
  pending_amount: number;
  completed_bookings: number;
  period_earnings: Array<{
    date: string;
    amount: number;
  }>;
}

export interface ProviderNotification {
  notification_id: number;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
}

export interface VerificationDocument {
  document_id: number;
  document_type: string;
  file_url: string;
  status: 'pending' | 'approved' | 'rejected';
  uploaded_at: string;
}

export interface BusinessProfile {
  // Primary Business Information
  business_name: string;
  description: string;
  about_me: string;
  business_type: 'individual' | 'small_business' | 'company' | 'llc' | 'corporation';
  provider_slug_url?: string;
  
  // Contact Information
  business_phone: string;
  business_email: string;
  website?: string;
  address?: string;
  city?: string;
  
  // Professional Details
  years_experience: number;
  specializations?: string;
  certifications?: string;
  
  // Business Registration & Legal
  business_license?: string;
  insurance_details?: string;
  
  // Profile & Display
  profile_image?: string;
  
  // User Information (if editing user details too)
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  preferred_language?: string;
}

// =============================================================================
// PROVIDER SERVICE CLASS
// =============================================================================

class ProviderService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/providers', // Keep /providers for admin management operations
      timeout: 15000,
    });
  }

  // =============================================================================
  // CORE PROVIDER MANAGEMENT (ADMIN)
  // =============================================================================

  async getProviders(filters: ProviderFilters = {}): Promise<ApiResponse<{
    providers: Provider[];
    pagination: any;
    stats?: ProviderStats;
  }>> {
    try {
      return await this.get('', { params: filters });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch providers');
    }
  }

  async getProviderById(id: number): Promise<ApiResponse<{ provider: Provider }>> {
    try {
      return await this.get(`/${id}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider');
    }
  }

  async createProvider(data: ProviderFormData): Promise<ApiResponse<{ provider: Provider }>> {
    try {
      return await this.post('', data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to create provider');
    }
  }

  async updateProvider(id: number, data: Partial<ProviderFormData>): Promise<ApiResponse<{ provider: Provider }>> {
    try {
      return await this.put(`/${id}`, data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update provider');
    }
  }

  async deleteProvider(id: number): Promise<ApiResponse<void>> {
    try {
      return await this.delete(`/${id}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete provider');
    }
  }

  // =============================================================================
  // PROVIDER VERIFICATION & STATUS
  // =============================================================================

  async verifyProvider(id: number): Promise<ApiResponse<{ provider: Provider }>> {
    try {
      return await this.post(`/${id}/verify`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to verify provider');
    }
  }

  async toggleProviderStatus(id: number): Promise<ApiResponse<{ provider: Provider }>> {
    try {
      return await this.post(`/${id}/toggle-status`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to toggle provider status');
    }
  }

  // =============================================================================
  // PROVIDER PROFILE MANAGEMENT
  // =============================================================================

  async getProfile(): Promise<ApiResponse<ProviderProfile>> {
    try {
      // Override baseEndpoint for profile calls - use /provider instead of /providers
      const originalEndpoint = this.baseEndpoint;
      this.baseEndpoint = '/provider';
      const result = await this.get('/profile');
      this.baseEndpoint = originalEndpoint; // Restore original
      return result;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider profile');
    }
  }

  async updateProviderProfile(data: Partial<BusinessProfile>): Promise<ApiResponse<ProviderProfile>> {
    try {
      // Override baseEndpoint for profile calls - use /provider instead of /providers
      const originalEndpoint = this.baseEndpoint;
      this.baseEndpoint = '/provider';
      
      const result = await this.put('/profile', data);
      this.baseEndpoint = originalEndpoint; // Restore original
      return result;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update provider profile');
    }
  }

  async uploadProfileImage(file: File): Promise<ApiResponse<{ url: string; media_id: number }>> {
    try {
      // Override baseEndpoint for profile calls - use /provider instead of /providers
      const originalEndpoint = this.baseEndpoint;
      this.baseEndpoint = '/provider';
      
      const formData = new FormData();
      formData.append('image', file); // Changed from 'profile_image' to 'image' to match backend
      
      const result = await this.post('/profile/upload-photo', formData);
      this.baseEndpoint = originalEndpoint; // Restore original
      return result;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to upload profile image');
    }
  }

  async checkSlugAvailability(slug: string): Promise<ApiResponse<{ available: boolean }>> {
    try {
      // Use direct API call for provider endpoint
      return await this.request('GET', `/check-slug/${slug}`, {}, { 
        skipAuth: false 
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to check slug availability');
    }
  }

  async updateSlug(slug: string): Promise<ApiResponse<{ slug: string }>> {
    try {
      // Use direct API call for provider endpoint
      return await this.request('PUT', '/slug', { slug }, { 
        skipAuth: false 
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update slug');
    }
  }

  async generateSlugSuggestions(businessName: string): Promise<ApiResponse<{ suggestions: string[] }>> {
    try {
      // Use direct API call for provider endpoint
      return await this.request('POST', '/suggest-slug', { business_name: businessName }, { 
        skipAuth: false 
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to generate slug suggestions');
    }
  }

  async getProviderBySlug(slug: string): Promise<ApiResponse<ProviderProfile>> {
    try {
      // Public endpoint - use direct path
      return await this.httpClient.get(`/api/public/provider/${slug}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider profile');
    }
  }

  async getProviderStats(): Promise<ApiResponse<ProviderStats>> {
    try {
      return await this.get('/stats');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider statistics');
    }
  }

  // =============================================================================
  // PROVIDER AREAS MANAGEMENT
  // =============================================================================

  async getProviderAreas(params: any = {}): Promise<ApiResponse<{
    areas: ServiceArea[];
    pagination?: any;
  }>> {
    try {
      return await this.get('/areas', { params });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider areas');
    }
  }

  async addServiceArea(data: Omit<ServiceArea, 'area_id' | 'provider_id' | 'created_at'>): Promise<ApiResponse<ServiceArea>> {
    try {
      return await this.post('/areas', data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to add service area');
    }
  }

  async updateServiceArea(areaId: number, data: Partial<ServiceArea>): Promise<ApiResponse<ServiceArea>> {
    try {
      return await this.put(`/areas/${areaId}`, data);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update service area');
    }
  }

  async deleteServiceArea(areaId: number): Promise<ApiResponse<{ message: string }>> {
    try {
      return await this.delete(`/areas/${areaId}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to delete service area');
    }
  }

  // =============================================================================
  // PROVIDER BOOKINGS MANAGEMENT
  // =============================================================================

  async getProviderBookings(params: any = {}): Promise<ApiResponse<{
    bookings: BookingRequest[];
    pagination?: any;
  }>> {
    try {
      return await this.get('/bookings', { params });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider bookings');
    }
  }

  async getBookingById(bookingId: number): Promise<ApiResponse<BookingRequest>> {
    try {
      return await this.get(`/bookings/${bookingId}`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch booking details');
    }
  }

  async updateBookingStatus(bookingId: number, status: string, notes?: string): Promise<ApiResponse<BookingRequest>> {
    try {
      return await this.put(`/bookings/${bookingId}/status`, { status, notes });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update booking status');
    }
  }

  // =============================================================================
  // PROVIDER REVIEWS MANAGEMENT
  // =============================================================================

  async getProviderReviews(params: any = {}): Promise<ApiResponse<{
    reviews: ProviderReview[];
    pagination?: any;
  }>> {
    try {
      return await this.get('/reviews', { params });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider reviews');
    }
  }

  async respondToReview(reviewId: number, response: string): Promise<ApiResponse<ProviderReview>> {
    try {
      return await this.post(`/reviews/${reviewId}/respond`, { response });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to respond to review');
    }
  }

  // =============================================================================
  // PROVIDER EARNINGS
  // =============================================================================

  async getProviderEarnings(period: string = 'month'): Promise<ApiResponse<ProviderEarnings>> {
    try {
      return await this.get('/earnings', { params: { period } });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider earnings');
    }
  }

  // =============================================================================
  // PROVIDER NOTIFICATIONS
  // =============================================================================

  async getProviderNotifications(params: any = {}): Promise<ApiResponse<{
    notifications: ProviderNotification[];
    pagination?: any;
  }>> {
    try {
      return await this.get('/notifications', { params });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch notifications');
    }
  }

  async markNotificationAsRead(notificationId: number): Promise<ApiResponse<{ message: string }>> {
    try {
      return await this.put(`/notifications/${notificationId}/read`);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to mark notification as read');
    }
  }

  // =============================================================================
  // PROVIDER VERIFICATION DOCUMENTS
  // =============================================================================

  async getVerificationDocuments(): Promise<ApiResponse<{
    documents: VerificationDocument[];
  }>> {
    try {
      return await this.get('/verification/documents');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch verification documents');
    }
  }

  async uploadVerificationDocument(documentType: string, file: File): Promise<ApiResponse<VerificationDocument>> {
    try {
      const formData = new FormData();
      formData.append('document_type', documentType);
      formData.append('file', file);
      
      return await this.post('/verification/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to upload verification document');
    }
  }

  // =============================================================================
  // PROVIDER WORKING HOURS
  // =============================================================================

  async getWorkingHours(): Promise<ApiResponse<{
    working_hours: WorkingHours[];
  }>> {
    try {
      return await this.get('/working-hours');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch working hours');
    }
  }

  async updateWorkingHours(workingHours: WorkingHours[]): Promise<ApiResponse<{
    working_hours: WorkingHours[];
  }>> {
    try {
      return await this.put('/working-hours', { working_hours: workingHours });
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update working hours');
    }
  }

  // =============================================================================
  // PROVIDER SETTINGS
  // =============================================================================

  async getProviderSettings(): Promise<ApiResponse<{
    settings: any;
  }>> {
    try {
      return await this.get('/settings');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to fetch provider settings');
    }
  }

  async updateProviderSettings(settings: any): Promise<ApiResponse<{
    settings: any;
  }>> {
    try {
      return await this.put('/settings', settings);
    } catch (error: any) {
      throw new Error(error.message || 'Failed to update provider settings');
    }
  }
}

// Create and export singleton instance
export const providerService = new ProviderService();
export default providerService;